import { useEffect, useRef, useState } from "react";
import { GetValue, PutValue, useIndexedDB } from "hooks/useIndexedDb";
import { Database } from "utils";
import { publishClipState } from "utils/clipRegistry";

interface AudioCacheItem {
    mediaId: string | null;
    folder: string | null;
    content: Blob;
    id: string;
}

interface UseAudioDataProps {
    /** The cue this audio belongs to, so its progress can be published for everyone to see. */
    id: string;
    mp3: string;
    /**
     * Готовить ли звук прямо сейчас.
     *
     * Раньше хук качал blob сразу при монтировании, а компонент строки монтируется у КАЖДОЙ
     * реплики папки. На папке в 72 реплики это около сотни запросов разом: вкладка переставала
     * отвечать — Chrome возвращал таймауты и на клик, и на снимок экрана (замер на лекции 8779,
     * папка 25). Теперь готовится только то, что видно или что просит расписание.
     */
    enabled?: boolean;
}

export const useAudioData = ({ id, mp3, enabled = true }: UseAudioDataProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [vaw, setVaw] = useState<string | null>(null);

    const { putValue, getValue, isDBConnecting } = useIndexedDB(Database.name);
    const objectUrlRef = useRef<string | null>(null);
    /** Which URL the object URL we are holding was made from, so it is fetched once. */
    const loadedForRef = useRef<string | null>(null);

    useEffect(() => {
        // A CUE WITH NO FILE IS A FAILURE, NOT A DOWNLOAD.
        //
        // `mp3` goes empty while a cue is being re-voiced. Fetching "" does not fail: it returns
        // the SPA's own index.html with status 200, which was then cached as this cue's audio.
        // Since the lecture now waits for a cue rather than giving up on it after 8s, a cue stuck
        // "loading" forever would stop the lecture outright — so say plainly that it cannot play.
        if (!mp3) {
            setIsLoading(false);
            setLoadError("нет файла");
            publishClipState(id, "error", "нет файла");
            return;
        }
        if (isDBConnecting) return;
        if (!enabled) {
            // Не «гружу», а «пока не нужно»: строка рисует место под волну и не занимает сеть.
            setIsLoading(false);
            return;
        }
        // Already holding this exact clip. A row can leave the prefetch window and come back
        // (scrolling, or the playhead moving); re-fetching and re-creating the element each time
        // would throw away readiness that had already been paid for.
        if (loadedForRef.current === mp3 && objectUrlRef.current) {
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        setIsLoading(true);
        setLoadError(null);
        publishClipState(id, "loading");

        const fetchData = async () => {
            try {
                const blob = await getAudioBlob(mp3, putValue, getValue);

                if (isMounted) {
                    // Revoke the PREVIOUS url here, not in this effect's cleanup. Cleanup runs on
                    // every dependency change — including one that leaves the same clip playing —
                    // and revoking a url the element is still pointing at kills it mid-sentence.
                    if (objectUrlRef.current) {
                        URL.revokeObjectURL(objectUrlRef.current);
                    }

                    const newUrl = URL.createObjectURL(blob);
                    objectUrlRef.current = newUrl;
                    loadedForRef.current = mp3;
                    setVaw(newUrl);
                    setIsLoading(false);
                    // NOT "ready" — that belongs to the element, which is the only thing that
                    // knows whether it can actually make sound (see AudioPlayerTrack).
                }
            } catch (error) {
                if (isMounted) {
                    console.error("Error fetching audio:", error);
                    const message = error instanceof Error ? error.message : "Unknown error";
                    setLoadError(message);
                    setIsLoading(false);
                    publishClipState(id, "error", message);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [id, mp3, isDBConnecting, enabled]);

    // The object URL outlives every dependency change and is released once, when the row goes.
    useEffect(
        () => () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        },
        [],
    );

    // Get audio blob with caching logic
    const getAudioBlob = async (
        audioUrl: string,
        putValue: PutValue,
        getValue: GetValue,
    ): Promise<Blob> => {
        try {
            // Try to get from IndexedDB first
            const cachedItem = await getValue(Database.audioTable, audioUrl);
            if (cachedItem && cachedItem.content) {
                return cachedItem.content;
            }

            // Fetch if not in cache
            const response = await fetch(audioUrl);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.status}`);
            }

            const blob = await response.blob();

            // Save to IndexedDB — but do not make first sound wait for the write. Storing is a
            // favour to the NEXT session; this session already has the bytes in hand.
            putValue(Database.audioTable, {
                mediaId: null,
                folder: null,
                content: blob,
                id: audioUrl,
            } as AudioCacheItem).catch(() => undefined);

            return blob;
        } catch (error) {
            console.error("Error in getAudioBlob:", error);
            throw error;
        }
    };

    return { isLoading, loadError, vaw };
};
